import type { Item, ItemPatch } from "./api.js";
import { completionDay, roll } from "./recurrence.js";

export interface OccurrenceCommand {
  operationId: string;
  expectedRevision: number;
  at?: number;
  timeZone?: string;
  completionId?: string;
}
export function occurrence(item: Item, kind: "complete" | "undo", command: OccurrenceCommand): Item {
  if (!item.repeat || !item.due) throw new Error("This item does not repeat.");
  if ((item.occurrenceRevision ?? 0) !== command.expectedRevision) throw new Error("This occurrence changed. Try again.");
  const next = { ...item, done: false, occurrenceRevision: command.expectedRevision + 1 };
  if (kind === "undo") {
    if (!item.completion || item.completion.id !== command.completionId) throw new Error("This completion can no longer be undone.");
    return { ...next, due: item.completion.due, lastDoneOn: item.completion.lastDoneOn, completion: null };
  }
  const today = completionDay(command.at!, command.timeZone!);
  return { ...next,
    due: { ...item.due, date: roll(item.due.date, item.repeat, today, item.repeatAnchor ?? item.due.date) },
    lastDoneOn: today,
    completion: { id: command.operationId, due: item.due, lastDoneOn: item.lastDoneOn ?? null },
  };
}

/** Missing fields in older mirrors mean non-repeating, without a destructive database upgrade. */
export function editOccurrence(item: Item, patch: ItemPatch): Item {
  const managed = item.repeat || patch.repeat || item.occurrenceRevision;
  if (!managed) return { ...item, ...patch };
  if (patch.done !== undefined && (item.repeat || patch.repeat)) throw new Error("Use the occurrence checkbox to complete this item.");
  const due = patch.due === undefined ? item.due : patch.due;
  const repeat = patch.due === null ? null : patch.repeat === undefined ? item.repeat ?? null : patch.repeat;
  if (repeat && !due) throw new Error("A repeating item needs a due date.");
  const reset = due?.date !== item.due?.date || repeat !== item.repeat;
  return { ...item, ...patch, repeat, done: repeat ? false : patch.done ?? item.done,
    repeatAnchor: repeat ? (reset ? due!.date : item.repeatAnchor ?? due!.date) : null,
    lastDoneOn: reset ? null : item.lastDoneOn ?? null,
    occurrenceRevision: (item.occurrenceRevision ?? 0) + 1, completion: null };
}
